<script setup lang="ts">
import type { PaypalWrapperProps } from './PaypalWrapper.props'

const props = withDefaults(defineProps<PaypalWrapperProps>(), {
  placement: 'product',
  styleTextSize: '10',
})

const appConfig = useAppConfig()
const { country } = useRouteHelper()
const { cart } = useCart()
const config = useRuntimeConfig()
const configs = useConfigs()
const { resolvePaypalMarketId, getPaypalExpressClientId } = usePaypalExpress()
const supportedCountries = ['DE', 'ES', 'FR', 'GB', 'IT', 'US']
const loaded = ref<boolean>(false)

// https://developer.paypal.com/limited-release/sdk-pay-later-messaging-cross-border/#link-buyercountry
const paypalCountry = computed(() => country.toUpperCase())
const isCountrySupported = computed(() =>
  supportedCountries.includes(paypalCountry.value)
)

const enabled = computed(
  () =>
    (!appConfig.currentMarketSettings.paypalBNPLMaxPriceCents ||
      props.amount < appConfig.currentMarketSettings.paypalBNPLMaxPriceCents) &&
    isCountrySupported.value
)

onMounted(async () => {
  if (enabled.value) {
    try {
      // Same client id as the Express button: a second SDK in the PayPalSDK
      // namespace destroys the first one's components.
      const marketId = await resolvePaypalMarketId(
        cart.value?.market?.id,
        configs.value.cl_market_code
      )
      const paypalScript = await usePaypalScript({
        clientId:
          getPaypalExpressClientId(marketId) || config.public.paypal.clientId,
        marketId: marketId ?? undefined,
        currency: cart.value?.currency_code!,
      })

      if (paypalScript.script) {
        await paypalScript.load()
        loaded.value = true
      }
    } catch (error) {
      console.error('Error loading PayPal for BNPL:', error)
    }
  }
})

const paypalAmount = computed(() => props.amount.toFixed(2))
</script>

<template>
  <div
    v-if="loaded"
    data-pp-message
    data-pp-style-text-color="monochrome"
    :data-pp-placement="placement"
    :data-pp-amount="paypalAmount"
    :data-pp-style-text-size="styleTextSize"
    :data-pp-buyercountry="paypalCountry"
  />
</template>
